import React from "react";
import { Circle, Square } from "lucide-react";
import { useMixer } from "@/context/MixerContext";
import RecordAndPublish from "@/components/RecordAndPublish";

export default function CentralMixer({ crossfader, setCrossfader, masterVolume, setMasterVolume, onNeuralMix, decks }) {
  const { engine, isRecording, recElapsed } = useMixer();

  React.useEffect(() => { if (engine) engine.setMasterVolume(masterVolume); }, [masterVolume, engine]);
  React.useEffect(() => { if (engine) engine.setCrossfader(crossfader); }, [crossfader, engine]);

  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="rounded-xl border border-[#2A354B] bg-gradient-to-b from-[#121620] to-[#0B0E14] p-4 h-full flex flex-col relative z-10" data-testid="central-mixer">
      <div className="text-center mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">// MASTER MIXER</div>
        <div className="font-display font-black text-2xl">NEURAL BRIDGE</div>
      </div>

      <div className="grid grid-cols-4 gap-2 mb-3">
        {["A", "B", "C", "D"].map(id => {
          const d = decks[id];
          const level = d.playing ? Math.min(1, d.volume * 0.9 + 0.1) : 0.05;
          return (
            <div key={id} className="text-center">
              <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: id === "A" ? "#00F0FF" : id === "B" ? "#FF8C00" : id === "C" ? "#00FF66" : "#FF0055" }}>{id}</div>
              <div className="h-24 w-4 mx-auto bg-[#0B0E14] border border-[#1E2638] rounded relative overflow-hidden mt-1">
                <div className="absolute bottom-0 left-0 right-0 transition-all" style={{ height: `${level * 100}%`, background: `linear-gradient(to top, #22C55E, #EAB308 70%, #EF4444)` }}/>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mb-4">
        <div className="flex justify-between text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mb-1">
          <span>Master</span><span>{Math.round(masterVolume * 100)}</span>
        </div>
        <input data-testid="master-volume-knob" type="range" min={0} max={1} step={0.01}
          value={masterVolume} onChange={e => setMasterVolume(Number(e.target.value))} className="dj-slider w-full"/>
      </div>

      <div className="mb-4">
        <div className="flex justify-between text-[10px] font-mono uppercase tracking-widest mb-1">
          <span className="text-[#00F0FF]">A / C</span>
          <span className="text-[#94A3B8]">Crossfader</span>
          <span className="text-[#FF8C00]">B / D</span>
        </div>
        <input data-testid="master-crossfader" type="range" min={0} max={1} step={0.01}
          value={crossfader} onChange={e => setCrossfader(Number(e.target.value))} className="dj-slider w-full"/>
      </div>

      <button data-testid="neural-mix-ai-button" onClick={onNeuralMix}
        className="w-full py-3 rounded-lg bg-gradient-to-r from-[#FF2A6D] via-[#FF8C00] to-[#00F0FF] text-black font-black uppercase tracking-[0.2em] text-sm hover:opacity-90 active:scale-95 transition-transform shadow-[0_0_30px_rgba(255,42,109,0.35)] mb-2"
      >⚡ Neural Auto-Mix</button>

      <RecordAndPublish decks={decks}/>

      <div className="mt-auto pt-4 border-t border-[#1E2638] font-mono text-[10px] text-[#94A3B8] uppercase tracking-widest space-y-1">
        <div className="flex justify-between"><span>Recording</span><span data-testid="master-recording-status" className={isRecording ? "text-[#EF4444]" : ""}>{isRecording ? `● REC ${fmtTime(recElapsed)}` : "● IDLE"}</span></div>
        <div className="flex justify-between"><span>Session BPM</span><span className="text-white">{
          (() => {
            const bpms = ["A", "B", "C", "D"].map(id => decks[id]).filter(d => d.playing && d.track && d.track.bpm).map(d => (d.track.bpm || 0) * (1 + d.pitch / 100));
            return bpms.length ? (bpms.reduce((a, b) => a + b, 0) / bpms.length).toFixed(1) : "--.-";
          })()
        }</span></div>
      </div>
    </div>
  );
}
